import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './ThankYouPage.module.css';
import { YOOKASSA_CONFIG } from '../../config/yookassa';

const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    nickname: '',
    birthDate: '',
    birthTime: '',
    birthCity: '',
    birthCountry: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Получаем email из URL параметров (если передан от Stripe)
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) {
      setFormData(prev => ({ ...prev, email: emailFromUrl }));
    }
  }, [searchParams]);

  useEffect(() => {
    const paymentId = localStorage.getItem('yookassa_payment_id');
    if (!paymentId) {
      return;
    }

    const fetchPaymentAmount = async () => {
      try {
        const response = await fetch(YOOKASSA_CONFIG.VERIFY_PAYMENT_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_id: paymentId }),
        });

        if (!response.ok) {
          throw new Error(`verify-payment failed: ${response.status}`);
        }

        const data = await response.json();
        if (data && data.amount && data.amount.value) {
          setPaymentAmount(data.amount.value);
        }
      } catch (error) {
        console.error('Ошибка при проверке платежа ЮKassa:', error);
      } finally {
        localStorage.removeItem('yookassa_payment_id');
      }
    };

    fetchPaymentAmount();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Отправка данных в Google Sheets через Google Apps Script
      const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbw995tpSlzZ-jwoeIyF7xG59Oov3zErtVGppeonxxaGOd-w8lE-vKI0414xrU1QQvVquQ/exec';
      
      // Подготавливаем данные для отправки
      const dataToSend = {
        timestamp: new Date().toISOString(),
        email: formData.email,
        nickname: formData.nickname,
        birthDate: formData.birthDate,
        birthTime: formData.birthTime || '',
        birthCity: formData.birthCity,
        birthCountry: formData.birthCountry,
        source: 'FORECAST2026',
        paymentAmount: paymentAmount || YOOKASSA_CONFIG.AMOUNT,
      };
      
      console.log('Отправка данных:', dataToSend);
      
      // Формируем URL с параметрами для GET запроса
      // Google Apps Script лучше всего принимает данные через URL параметры
      const params = new URLSearchParams();
      Object.keys(dataToSend).forEach(key => {
        params.append(key, dataToSend[key]);
      });
      
      const urlWithParams = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
      console.log('URL с параметрами:', urlWithParams);
      
      // Отправляем через GET запрос с mode: 'no-cors'
      // Google Apps Script получит данные через e.parameter из URL
      await fetch(urlWithParams, {
        method: 'GET',
        mode: 'no-cors'
      });

      // При mode: 'no-cors' мы не можем проверить ответ,
      // но если запрос не выбросил исключение - данные отправились
      console.log('✅ Запрос отправлен. Проверьте Google Sheets.');
      
      // Показываем успех
      setSubmitStatus('success');
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      
      // Показываем детали ошибки в консоли для отладки
      console.error('Детали ошибки:', {
        message: error.message,
        stack: error.stack,
        formData: formData
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Если форма успешно отправлена, показываем только сообщение об успехе
  if (submitStatus === 'success') {
    return (
      <div className={styles.thankYouPage}>
        <div className={styles.container}>
          {/* Картинка-поздравление */}
          <div className={styles.congratulationsImage}>
            <img 
              src="https://strategy.annavision.me/wp-content/uploads/2025/12/1766996988772-9sqe11pqa9a.png" 
              alt="Поздравляем с этим важным шагом для себя. 2026 принесет ощущение внутренней опоры и ясности, мягко приблизит вас к желаемым результатам в легкости." 
              className={styles.congratsImg}
            />
          </div>

          {/* Сообщение об успехе */}
          <div className={styles.successContainer}>
            <h1 className={styles.title}>
              <span className="gold-text">✅ Анкета успешно отправлена!</span>
            </h1>
            <div className={styles.successMessage}>
              <p className={styles.successText}>
                Спасибо! Ваши данные получены. Мы начнем расчет вашего персонального прогноза.
              </p>
              <p className={styles.successNote}>
                Через 21 день вы получите детальный навигатор на весь 2026 год на указанный email.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Показываем форму, если еще не отправлено успешно
  return (
    <div className={styles.thankYouPage}>
      <div className={styles.container}>
        {/* Картинка-поздравление */}
        <div className={styles.congratulationsImage}>
          <img 
            src="https://strategy.annavision.me/wp-content/uploads/2025/12/1766996988772-9sqe11pqa9a.png" 
            alt="Поздравляем с этим важным шагом для себя. 2026 принесет ощущение внутренней опоры и ясности, мягко приблизит вас к желаемым результатам в легкости." 
            className={styles.congratsImg}
          />
        </div>

        {/* Заголовок */}
        <h1 className={styles.title}>
          <span className="gold-text">Спасибо за ваш заказ!</span>
        </h1>
        <p className={styles.subtitle}>
          Ваш платеж подтвержден. Пожалуйста, заполните анкету, чтобы мы могли рассчитать ваш персональный прогноз.
        </p>

        {/* Форма */}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email <span className={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={styles.input}
              placeholder="Введите email, который вы использовали при оплате"
            />
            <p className={styles.helpText}>Укажите email, который вы использовали при оплате в Stripe</p>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="nickname" className={styles.label}>
              Как к вам можно обращаться? (ваш ник) <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              required
              className={styles.input}
              placeholder="Ваше имя или ник"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="birthDate" className={styles.label}>
              Дата рождения <span className={styles.required}>*</span>
            </label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="birthTime" className={styles.label}>
              Час рождения (если известен)
            </label>
            <input
              type="time"
              id="birthTime"
              name="birthTime"
              value={formData.birthTime}
              onChange={handleChange}
              className={styles.input}
              placeholder="Опционально"
            />
            <p className={styles.helpText}>Можно оставить пустым, если не знаете точное время</p>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="birthCity" className={styles.label}>
              Город рождения <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="birthCity"
              name="birthCity"
              value={formData.birthCity}
              onChange={handleChange}
              required
              className={styles.input}
              placeholder="Например: Москва"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="birthCountry" className={styles.label}>
              Страна рождения <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="birthCountry"
              name="birthCountry"
              value={formData.birthCountry}
              onChange={handleChange}
              required
              className={styles.input}
              placeholder="Например: Россия"
            />
          </div>

          {submitStatus === 'error' && (
            <div className={styles.errorMessage}>
              ❌ Произошла ошибка. Пожалуйста, попробуйте еще раз или свяжитесь с нами.
            </div>
          )}

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Отправка...' : 'Отправить анкету'}
          </button>
        </form>

        <p className={styles.note}>
          После отправки анкеты мы начнем расчет вашего персонального прогноза. 
          Через 21 день вы получите детальный навигатор на весь 2026 год на указанный email.
        </p>
      </div>
    </div>
  );
};

export default ThankYouPage;
